import { isDatasetLinkValue } from '@blaze-shared/schema-domain';
import { isNullish } from '@blaze-shared/shared-util';

/** Caps how many discrete score options are rendered. */
export const MAX_SCORE_OPTIONS = 50;

export function parseOptionalNumber(next: unknown): number | undefined {
  if (isDatasetLinkValue(next)) {
    next = Number(next.display);
  }
  if (isNullish(next) || next === '' || Number.isNaN(Number(next))) {
    return undefined;
  }
  return Number(next);
}

export function dataValueToScoreOption(value: number): number {
  return Math.round(value);
}

export function resolveScoreBounds(
  configuredMin: number | string | undefined,
  configuredMax: number | string | undefined,
): { min: number; max: number } {
  const parsedMin = configuredMin !== '' ? Number(configuredMin) : NaN;
  const parsedMax = configuredMax !== '' ? Number(configuredMax) : NaN;
  const min = Number.isFinite(parsedMin) ? Math.round(parsedMin) : 1;
  const max = Number.isFinite(parsedMax) ? Math.round(parsedMax) : 5;
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return { min: lo, max: Math.min(hi, lo + MAX_SCORE_OPTIONS - 1) };
}

export function getScoreValues(min: number, max: number): number[] {
  const bounds = resolveScoreBounds(min, max);
  const values: number[] = [];
  for (let value = bounds.min; value <= bounds.max; value++) {
    values.push(value);
  }
  return values;
}

/**
 * Bound form value wins (rounded onto an option). Otherwise the optional default
 * is used when it falls within the options.
 */
export function resolveCurrentScore(
  boundValue: number | undefined,
  defaultValue: number | undefined,
  values: number[],
): number | undefined {
  if (boundValue != null) {
    const option = dataValueToScoreOption(boundValue);
    return values.includes(option) ? option : undefined;
  }
  if (defaultValue != null && values.includes(defaultValue)) {
    return defaultValue;
  }
  return undefined;
}

export function getAdjacentScoreValue(
  values: number[],
  current: number | undefined,
  direction: 1 | -1,
): number | undefined {
  if (values.length === 0) {
    return undefined;
  }
  if (current == null) {
    return direction === 1 ? values[0] : values.at(-1);
  }
  const index = values.indexOf(current);
  if (index === -1) {
    return direction === 1 ? values[0] : values.at(-1);
  }
  const nextIndex = (index + direction + values.length) % values.length;
  return values[nextIndex];
}

export function getScoreTabIndex(
  value: number,
  selected: number | undefined,
  values: number[],
): number {
  if (selected != null) {
    return value === selected ? 0 : -1;
  }
  return value === values[0] ? 0 : -1;
}
